/**
 * Date-range label constants and helpers used by the report wizard and
 * the reverse-parse path in nativeDataTransforms.ts.
 *
 * The FORWARD path does NO date arithmetic at all: a dateRange label compiles
 * to a pair of relative-date TOKENS (see DATE_RANGE_TOKENS), which the server
 * resolves against the org's calendar at execution time. That is what makes a
 * saved report's window keep rolling, and it removes a whole class of bug —
 * the browser's calendar deciding a shared, org-wide window. A report saved at
 * 23:59 and one saved at 00:01 now produce byte-identical queries.
 *
 * The UTC arithmetic below is LEGACY ONLY: it exists so an already-saved report
 * whose query holds absolute literals still shows its chip when reopened.
 * `dateRangeToFiltersLegacy`, `matchDateRangeLabel` and
 * `matchDateRangeLabelAnchored` serve that path and nothing else — do not reach
 * for them from new code.
 *
 * IMPORTANT: No Date-derived value may be computed at module scope here.
 * All date arithmetic must stay inside function bodies so a tab left open
 * across midnight always reads fresh "today" values.
 */

import type { RelativeDateValue } from '@/types/reports';

/** Format a Date as YYYY-MM-DD using UTC calendar components. */
function toISODate(d: Date): string {
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * The dateRange dropdown labels FieldsStep can produce. Must stay in sync with
 * the cases handled by dateRangeToFilters() below — an unknown label simply
 * yields no filters and therefore never matches during reverse parsing.
 */
export const DATE_RANGE_LABELS = [
  'Last 7 days',
  'Last 30 days',
  'This month',
  'Last month',
  'This quarter',
  'This year',
  'from now to 30 days from now',
  'from 1 year ago to yesterday',
] as const;

const rel = (
  unit: RelativeDateValue['$relative']['unit'],
  offset: number,
  snap?: 'start' | 'end'
): RelativeDateValue => ({
  $relative: snap ? { unit, offset, snap } : { unit, offset },
});

/**
 * Every dateRange label, as the gte/lte relative-date token pair it compiles to.
 *
 * A plain module-level constant with no `Date` in sight — that is the point.
 * These pairs reproduce exactly what `dateRangeToFiltersLegacy` computes for
 * any given anchor day; the parity is pinned from the backend side by
 * `apps/crm_events/core/tests/test_relative_dates.py::PresetParityTests`.
 */
export const DATE_RANGE_TOKENS: Record<
  (typeof DATE_RANGE_LABELS)[number],
  { gte: RelativeDateValue; lte: RelativeDateValue }
> = {
  'Last 7 days': { gte: rel('day', -6), lte: rel('day', 0) },
  'Last 30 days': { gte: rel('day', -29), lte: rel('day', 0) },
  'This month': {
    gte: rel('month', 0, 'start'),
    lte: rel('month', 0, 'end'),
  },
  'Last month': {
    gte: rel('month', -1, 'start'),
    lte: rel('month', -1, 'end'),
  },
  'This quarter': {
    gte: rel('quarter', 0, 'start'),
    lte: rel('quarter', 0, 'end'),
  },
  'This year': { gte: rel('year', 0, 'start'), lte: rel('year', 0, 'end') },
  'from now to 30 days from now': { gte: rel('day', 0), lte: rel('day', 30) },
  'from 1 year ago to yesterday': { gte: rel('year', -1), lte: rel('day', -1) },
};

/**
 * Convert a dateRange string from a groupBy entry into property filter(s).
 *
 * The dateRange dropdown options in FieldsStep produce strings like
 * "Last 7 days", "This month", "from now to 30 days from now", etc.
 * These become gte/lte filter conditions on the property so the native backend
 * actually scopes the query by date — carrying relative TOKENS, not literals,
 * so the window is re-evaluated on every execution instead of being frozen at
 * save time.
 *
 * Pure and clock-independent: a lookup in DATE_RANGE_TOKENS, no `new Date()`.
 * Returns an array of filter objects (0 entries for an unknown label, else 2).
 */
export function dateRangeToFilters(
  property: string,
  dateRange: string
): Array<{ property: string; operator: string; value: RelativeDateValue }> {
  // Own-property check, not a truthiness test: an inherited key such as
  // "__proto__" or "toString" resolves to something truthy on a plain object
  // and would emit filters with undefined values.
  if (!Object.hasOwn(DATE_RANGE_TOKENS, dateRange)) {
    return [];
  }
  const tokens =
    DATE_RANGE_TOKENS[dateRange as (typeof DATE_RANGE_LABELS)[number]];
  return [
    { property, operator: 'gte', value: tokens.gte },
    { property, operator: 'lte', value: tokens.lte },
  ];
}

/**
 * JSON with object keys sorted at every depth, so two structurally equal
 * values serialise identically whatever order their keys were written in.
 * The replacer runs on each nested value too, so one pass sorts the whole tree.
 */
function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, nested: unknown) =>
    nested !== null && typeof nested === 'object' && !Array.isArray(nested)
      ? Object.fromEntries(
          Object.entries(nested).sort(([a], [b]) => a.localeCompare(b))
        )
      : nested
  );
}

/**
 * Reverse a set of gte/lte token filters on one property back into the
 * dateRange label that produced them.
 *
 * Structural equality against DATE_RANGE_TOKENS — so unlike the literal
 * matchers below it needs NO anchor day at all, and cannot mislabel a window.
 * Order-insensitive (the caller's filter order is not guaranteed).
 *
 * The `{day,0}` / `{day,0,snap:'start'}` / `{day,0,snap:'end'}` ambiguity (all
 * three mean today) is harmless here: the wizard only ever emits the eight
 * pairs in the table, so the match is total over what it produces. Do not
 * "fix" it by canonicalising meaning — a token that is not in the table is
 * *supposed* to fall through to the literal-condition path.
 *
 * Key ORDER is a different matter and is normalised: `{snap, offset, unit}`
 * and `{unit, offset, snap}` are the same token, and a payload that arrives
 * from the API in another order must still reconstruct its chip.
 *
 * Returns null when no label reproduces the filters — the caller then leaves
 * them as literal conditions, which is graceful degradation, never wrong
 * results.
 */
export function matchRelativeDateRange(
  property: string,
  dateFilters: Array<{ operator: string; value: unknown }>
): string | null {
  if (dateFilters.length !== 2) {
    return null;
  }
  const target = new Set(
    dateFilters.map(f => `${f.operator}|${stableStringify(f.value)}`)
  );
  for (const label of DATE_RANGE_LABELS) {
    const produced = dateRangeToFilters(property, label);
    if (
      produced.every(p =>
        target.has(`${p.operator}|${stableStringify(p.value)}`)
      )
    ) {
      return label;
    }
  }
  return null;
}

/**
 * The pre-token forward path, kept verbatim for the LEGACY reverse matchers.
 *
 * @deprecated Legacy absolute reports only. New ranges compile to relative
 * tokens via `dateRangeToFilters` and are resolved server-side in the org's
 * calendar; this exists solely so `matchDateRangeLabel` can recompute the
 * literals an already-saved query baked in and still show its chip.
 *
 * `referenceDate` overrides "today" as the anchor the relative range is
 * evaluated against — the day a query was SAVED, so its baked-in date pair is
 * recognizable.
 */
export function dateRangeToFiltersLegacy(
  property: string,
  dateRange: string,
  referenceDate?: Date
): Array<{ property: string; operator: string; value: string }> {
  // Anchor at UTC midnight of the reference instant (see module doc: the
  // UTC calendar is the org-shared calendar for report windows).
  const now = referenceDate ? new Date(referenceDate) : new Date();
  now.setUTCHours(0, 0, 0, 0);
  const today = toISODate(now);

  switch (dateRange) {
    case 'Last 7 days': {
      // Inclusive of today: today + the 6 prior days = 7 calendar days.
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - 6);
      return [
        { property, operator: 'gte', value: toISODate(d) },
        { property, operator: 'lte', value: today },
      ];
    }
    case 'Last 30 days': {
      // Inclusive of today: today + the 29 prior days = 30 calendar days.
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() - 29);
      return [
        { property, operator: 'gte', value: toISODate(d) },
        { property, operator: 'lte', value: today },
      ];
    }
    case 'This month': {
      const start = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
      );
      // Day 0 of the next month = the last day of this month.
      const end = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)
      );
      return [
        { property, operator: 'gte', value: toISODate(start) },
        { property, operator: 'lte', value: toISODate(end) },
      ];
    }
    case 'Last month': {
      const start = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
      );
      const end = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)
      );
      return [
        { property, operator: 'gte', value: toISODate(start) },
        { property, operator: 'lte', value: toISODate(end) },
      ];
    }
    case 'This quarter': {
      const qMonth = Math.floor(now.getUTCMonth() / 3) * 3;
      const start = new Date(Date.UTC(now.getUTCFullYear(), qMonth, 1));
      const end = new Date(Date.UTC(now.getUTCFullYear(), qMonth + 3, 0));
      return [
        { property, operator: 'gte', value: toISODate(start) },
        { property, operator: 'lte', value: toISODate(end) },
      ];
    }
    case 'This year': {
      const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
      const end = new Date(Date.UTC(now.getUTCFullYear(), 11, 31));
      return [
        { property, operator: 'gte', value: toISODate(start) },
        { property, operator: 'lte', value: toISODate(end) },
      ];
    }
    case 'from now to 30 days from now': {
      const d = new Date(now);
      d.setUTCDate(d.getUTCDate() + 30);
      return [
        { property, operator: 'gte', value: today },
        { property, operator: 'lte', value: toISODate(d) },
      ];
    }
    case 'from 1 year ago to yesterday': {
      const start = new Date(now);
      start.setUTCFullYear(start.getUTCFullYear() - 1);
      const yesterday = new Date(now);
      yesterday.setUTCDate(yesterday.getUTCDate() - 1);
      return [
        { property, operator: 'gte', value: toISODate(start) },
        {
          property,
          operator: 'lte',
          value: toISODate(yesterday),
        },
      ];
    }
    default:
      return [];
  }
}

/**
 * Reverse a set of gte/lte date filters on a single property back into the
 * dateRange label that produced them.
 *
 * dateRangeToFiltersLegacy() is deterministic given an anchor day, so we re-run
 * it for each known label and return the one whose output exactly matches the
 * supplied filters. Pass `referenceDate` (typically the report's updated_at)
 * to evaluate labels as of the day the query was SAVED — that is what lets a
 * "Last 7 days" chip survive a reopen on a later day instead of degrading to
 * frozen literal dates. Returns null when no label reproduces the filters
 * (hand-authored dates, or an anchor that no longer lines up) — the caller
 * then leaves the filters as-is, which is graceful degradation, never wrong
 * results.
 */
export function matchDateRangeLabel(
  property: string,
  dateFilters: Array<{ operator: string; value: unknown }>,
  referenceDate?: Date
): string | null {
  const target = new Set(
    dateFilters.map(f => `${f.operator}|${String(f.value)}`)
  );
  for (const label of DATE_RANGE_LABELS) {
    const produced = dateRangeToFiltersLegacy(property, label, referenceDate);
    if (produced.length !== target.size) {
      continue;
    }
    if (produced.every(p => target.has(`${p.operator}|${p.value}`))) {
      return label;
    }
  }
  return null;
}

/**
 * Anchor-tolerant variant of matchDateRangeLabel for SAVED queries.
 *
 * `referenceDate` is the report's updated_at. Since ranges are derived on
 * the UTC calendar, a query saved today matches exactly at the anchor's
 * UTC day. The ±1-day candidates recover queries saved BEFORE the UTC
 * switch, whose literals were derived from the saver's local calendar day
 * — at most one day either side of the UTC day of the same instant.
 *
 * Returns null when no label matches on any candidate day — the caller
 * keeps the filters as literal conditions (graceful degradation, never a
 * silently shifted range).
 */
export function matchDateRangeLabelAnchored(
  property: string,
  dateFilters: Array<{ operator: string; value: unknown }>,
  referenceDate: Date
): string | null {
  for (const dayOffset of [0, -1, 1]) {
    const candidate = new Date(referenceDate);
    candidate.setUTCDate(candidate.getUTCDate() + dayOffset);
    const label = matchDateRangeLabel(property, dateFilters, candidate);
    if (label) {
      return label;
    }
  }
  return null;
}
